import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export const GA4_MEASUREMENT_ID = "G-S574RV39L0";

export function ga4Event(name: string, params?: Record<string, unknown>) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", name, params ?? {});
}

/**
 * Sends a GA4 page_view on every client-side route change (SPA).
 * The initial page_view is sent automatically by the gtag config snippet.
 */
export function useGa4PageViews() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.searchStr });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    const page_path = `${pathname}${search ?? ""}`;
    window.gtag("event", "page_view", {
      page_path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search]);
}

/**
 * Global delegated listener for click-to-call links (`<a href="tel:...">`).
 */
export function useGa4ClickToCall() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest("a[href^='tel:']") as HTMLAnchorElement | null;
      if (!anchor) return;
      const number = anchor.getAttribute("href")?.replace(/^tel:/, "") ?? "";
      ga4Event("click_to_call", {
        phone_number: number,
        link_text: anchor.textContent?.trim().slice(0, 100) ?? "",
        page_path: window.location.pathname,
      });
    };
    document.addEventListener("click", handler, { capture: true });
    return () => document.removeEventListener("click", handler, { capture: true } as EventListenerOptions);
  }, []);
}
